import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  AppBar,
  Tab,
  Tabs,
  Typography,
  Box,
  Container,
  Paper,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import {
  Assessment,
  BarChart,
  PieChart,
  TrendingUp,
  Business,
  Payment,
  Receipt,
  ShowChart
} from '@material-ui/icons'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'

// Components
import Recolte from '../../components/Recolte'
import SearchVentes from '../../components/SearchVentes'
import SearchRProduit from '../../components/SearchRProduit'
import ChartPage from '../../components/ChartPage'
import StatsModePaiement from '../../components/StatModePayement'
import Bilan from '../../components/Bilan'

// Styles harmonisés avec les composants Client et User
const useStyles = makeStyles((theme) => ({
  pageContainer: {
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    paddingTop: theme.spacing(1),
  },
  appBar: {
    background: 'linear-gradient(135deg, #226D68 0%, #1a524e 100%)',
    borderRadius: '12px 12px 0 0',
    boxShadow: '0 4px 20px rgba(34, 109, 104, 0.3)',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '85%',
  },
  tabs: {
    '& .MuiTab-root': {
      minWidth: 120,
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      color: theme.palette.common.white,
      opacity: 0.8,
      '&.Mui-selected': {
        opacity: 1,
        color: theme.palette.common.white,
      },
      '&:hover': {
        opacity: 1,
        backgroundColor: 'rgba(242, 101, 34, 0.1)',
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#f26522',
      height: 3,
    },
  },
  title: {
    background: 'linear-gradient(135deg, #226D68 0%, #2a8c82 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: theme.spacing(2), 
    marginTop: theme.spacing(1), 
    fontSize: '2rem', 
  },
  tabContainer: {
    padding: theme.spacing(4),
    backgroundColor: 'white',
    borderRadius: '0 0 16px 16px',
    boxShadow: '0 4px 20px rgba(34, 109, 104, 0.08)',
    minHeight: '500px',
    border: `1px solid #e0e0e0`,
    borderTop: 'none',
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '85%',
  },
  statChip: {
    borderRadius: '20px',
    fontWeight: 600,
    margin: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
  accessDeniedCard: {
    background: 'white',
    borderRadius: 16,
    padding: theme.spacing(4),
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(34, 109, 104, 0.08)',
    margin: '0 auto',
    width: '85%',
    border: `1px solid #e0e0e0`,
  },
}));

const StyledTabs = withStyles({
  indicator: {
    backgroundColor: '#f26522',
    height: 3,
    borderRadius: 2,
  },
})(Tabs);

const StyledTab = withStyles((theme) => ({
  root: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    marginRight: theme.spacing(1),
    color: 'rgba(255, 255, 255, 0.7)',
    '&$selected': {
      color: '#fff',
    },
    '&:focus': {
      opacity: 1,
    },
  },
  selected: {},
}))((props) => <Tab disableRipple {...props} />);

const TabContainer = ({ children, value, index, ...other }) => {
  const classes = useStyles();
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      className={classes.tabContainer}
      {...other}
    >
      {value === index && children}
    </div>
  );
};

TabContainer.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const Statistiques = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const auth = useSelector((state) => state.auth);
  const [value, setValue] = useState(0);

  // Récupération des feature flags
  const featureFlags = {
    modePaiement: process.env.REACT_APP_FEATURE_FLIPPING_MODE_PAYEMENT === "true",
    bilanProdVendu: process.env.REACT_APP_FEATURE_FLIPPING_BILAN_PRODVENDU === "true"
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // Configuration des tabs avec icônes comme dans les autres composants
  const tabsConfig = [
    { 
      label: "Mes récoltes", 
      icon: <Business />,
      visible: true, 
      component: <Recolte /> 
    },
    { 
      label: "Ventes gérants", 
      icon: <Receipt />,
      visible: true, 
      component: <SearchVentes /> 
    },
    { 
      label: "Produits vendus", 
      icon: <Assessment />,
      visible: true, 
      component: <SearchRProduit /> 
    },
    { 
      label: "Top 10 produits", 
      icon: <TrendingUp />,
      visible: true, 
      component: <ChartPage /> 
    },
    { 
      label: "Modes paiement", 
      icon: <Payment />,
      visible: featureFlags.modePaiement, 
      component: <StatsModePaiement /> 
    },
    { 
      label: "Bilan", 
      icon: <BarChart />,
      visible: featureFlags.bilanProdVendu, 
      component: <Bilan /> 
    }
  ];

  const visibleTabs = tabsConfig.filter(tab => tab.visible);

  return (
    <div className={classes.pageContainer}>
      <Container maxWidth="xl">
        {/* Titre centré exactement comme dans les autres composants */}
        <Typography variant="h3" className={classes.title}>
          Tableau de Bord Statistiques
        </Typography>

        {/* Navigation par onglets - seulement pour les admins */}
        {auth.rol === "admin" ? (
          <Paper elevation={0}>
            <AppBar position="static" className={classes.appBar}>
              <StyledTabs 
                value={value} 
                onChange={handleChange}
                className={classes.tabs}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons="auto"
                centered
              >
                {visibleTabs.map((tab, index) => (
                  <StyledTab
                    key={index}
                    icon={tab.icon}
                    label={isMobile ? null : tab.label}
                    aria-label={tab.label}
                  />
                ))}
              </StyledTabs>
            </AppBar>

            {/* Contenu des onglets */}
            {visibleTabs.map((tab, index) => (
              <TabContainer key={index} value={value} index={index}>
                {tab.component}
              </TabContainer>
            ))}
          </Paper>
        ) : (
          /* Message pour les non-admins */
          <Card className={classes.accessDeniedCard}>
            <CardContent>
              <Assessment style={{ fontSize: 64, color: '#226D68', marginBottom: 16 }} />
              <Typography variant="h5" color="textSecondary" gutterBottom>
                Accès Restreint aux Statistiques
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Seuls les administrateurs peuvent accéder au tableau de bord statistiques.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Indicateurs de statut en bas de page - seulement pour les admins */}
        {auth.rol === "admin" && (
          <>
            <Box mt={3} display="flex" justifyContent="center" flexWrap="wrap">
              <Chip
                icon={<Business style={{ color: '#226D68' }} />}
                label={`${visibleTabs.length} modules statistiques`}
                className={classes.statChip}
                style={{ 
                  backgroundColor: '#E8F5E8', 
                  color: '#226D68',
                  border: '1px solid #226D68'
                }}
              />
              <Chip
                icon={<TrendingUp style={{ color: '#f26522' }} />}
                label="Données en temps réel"
                className={classes.statChip}
                style={{ 
                  backgroundColor: '#FFF3E0', 
                  color: '#f26522',
                  border: '1px solid #f26522'
                }}
              />
              <Chip
                icon={<BarChart style={{ color: '#dc3545' }} />}
                label={`${featureFlags.modePaiement ? 'Mode paiement' : ''} ${featureFlags.bilanProdVendu ? 'Bilan activé' : ''}`}
                className={classes.statChip}
                style={{ 
                  backgroundColor: '#FFEBEE', 
                  color: '#dc3545',
                  border: '1px solid #dc3545'
                }}
              />
            </Box>

            {/* Footer décoratif */}
            <Box mt={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                Système d'analyse statistique • 
                <span style={{ color: '#226D68', fontWeight: 600 }}> Vert</span>
                <span style={{ color: '#f26522', fontWeight: 600 }}> Orange</span>
                {' '}Theme
              </Typography>
            </Box>
          </>
        )}
      </Container>
    </div>
  )
}

export default Statistiques